import { Injectable } from '@angular/core';
import {
    IAdfAppModelConfigurationItem,
    IPluginMetadataSucceededResult,
    IPluginSettingsProperty, IUIControlsItem
} from 'app/apis/plugin-metadata-api.service';
import { Named, Owned } from 'shell-models-lib';

const AUTO_GENERATED_PROPERTY_PREFIX = 'PLUGIN_METADATA_PROCESS_SERVICE_AUTO_GENERATED_PROPERTY';

@Injectable({
    providedIn: 'root'
})
export class PluginMetadataProcessService {
    public static className = 'PluginMetadataProcessService';
    public static RESERVED_WORDS = new Set([
        'owner',
        'conceptOwner',
        'concept',
        'rootOwner',
        'named',
        'displayName',
        'name',
        'raw',
        'loadError',
        'status',
        'isGitMismatch',
        'id',
        'extension',
        'isOk',
        'isBad',
        'isDependentOnBadResource'
    ]);

    // connectorName -> (propertyName -> resourceName)
    private readonly _propertyNameToResourceNameMapping: Map<string, Map<string, string>> = new Map<string, Map<string, string>>();

    public preProcessConfigurations(pluginMetadataList: IPluginMetadataSucceededResult[]): void {
        pluginMetadataList.forEach(this.preProcessConfiguration, this);
    }

    /**
     * replace the reserved words in configuration
     * @param pluginMetadata plugin definition
     */
    public preProcessConfiguration(pluginMetadata: IPluginMetadataSucceededResult): void {
        if (pluginMetadata && !this._propertyNameToResourceNameMapping.has(pluginMetadata.connectorName)) {
            const propertyNameToResourceNameMapping: Map<string, string> = new Map<string, string>();
            const resourceNameToPropertyNameMapping: Map<string, string> = new Map<string, string>();

            const checkAndReplaceReservedWordsInPluginSettingsProperty = (properties: IPluginSettingsProperty[]): void => {
                if (Array.isArray(properties)) {
                    properties.forEach((property) => {
                        const { name: resourceName } = property;
                        if (this._isReservedWord(resourceName)) {
                            const propertyName = resourceNameToPropertyNameMapping.has(resourceName)
                                ? resourceNameToPropertyNameMapping.get(resourceName)
                                : `${AUTO_GENERATED_PROPERTY_PREFIX}_${Math.random().toString(36).slice(2)}_${resourceName}`.toUpperCase();
                            property.name = propertyName;
                            propertyNameToResourceNameMapping.set(propertyName, resourceName);
                            resourceNameToPropertyNameMapping.set(resourceName, propertyName);
                        }
                    });
                }
            };
            checkAndReplaceReservedWordsInPluginSettingsProperty(pluginMetadata.connectionSettings);
            checkAndReplaceReservedWordsInPluginSettingsProperty(pluginMetadata.readSettings);
            checkAndReplaceReservedWordsInPluginSettingsProperty(pluginMetadata.writeSettings);

            const checkAndReplaceReservedWordsInUIControlsItems = (items: IUIControlsItem[]): void => {
                if (Array.isArray(items)) {
                    items.forEach((item) => {
                        if (item.bind != null) {
                            if (typeof item.bind === 'string') {
                                if (resourceNameToPropertyNameMapping.has(item.bind)) {
                                    item.bind = resourceNameToPropertyNameMapping.get(item.bind);
                                }
                            } else {
                                Object.entries(item.bind).forEach(([key, value]) => {
                                    if (resourceNameToPropertyNameMapping.has(value)) {
                                        (item.bind as { [type: string]: string })[key] = resourceNameToPropertyNameMapping.get(value);
                                    }
                                });
                            }
                        }
                        if (item.controlSettings?.properties) {
                            checkAndReplaceReservedWordsInUIControlsItems(item.controlSettings.properties);
                        }
                        const includedSettingsKeys = item.controlSettings?.browseSettings?.includedSettingsKeys;
                        if (includedSettingsKeys) {
                            for (let i = 0; i < includedSettingsKeys.length; i += 1) {
                                if (resourceNameToPropertyNameMapping.has(includedSettingsKeys[i])) {
                                    includedSettingsKeys[i] = resourceNameToPropertyNameMapping.get(includedSettingsKeys[i]);
                                }
                            }
                        }
                    });
                }
            };

            const checkAndReplaceReservedWordsInAdfAppModelConfigurationItem = (item: IAdfAppModelConfigurationItem): void => {
                if (item) {
                    if (Array.isArray(item.properties)) {
                        for (let i = 0; i < item.properties.length; i += 1) {
                            if (resourceNameToPropertyNameMapping.has(item.properties[i])) {
                                item.properties[i] = resourceNameToPropertyNameMapping.get(item.properties[i]);
                            }
                        }
                    }
                    checkAndReplaceReservedWordsInUIControlsItems(item.uiControls);
                    if (Array.isArray(item.itemSettings?.dataflowConnectTransformationProperties)) {
                        for (let i = 0; i < item.itemSettings.dataflowConnectTransformationProperties.length; i += 1) {
                            if (resourceNameToPropertyNameMapping.has(item.itemSettings.dataflowConnectTransformationProperties[i])) {
                                item.itemSettings.dataflowConnectTransformationProperties[i] = resourceNameToPropertyNameMapping.get(item.itemSettings.dataflowConnectTransformationProperties[i]);
                            }
                        }
                    } else if (typeof item.itemSettings?.dataflowConnectTransformationProperties === 'object' && item.itemSettings?.dataflowConnectTransformationProperties !== null) {
                        const dataflowConnectTransformationProperties: Record<string, string> = {};
                        Object.entries(item.itemSettings.dataflowConnectTransformationProperties).forEach(([key, value]) => {
                            dataflowConnectTransformationProperties[resourceNameToPropertyNameMapping.get(key) ?? key] = value;
                        });
                        item.itemSettings.dataflowConnectTransformationProperties = dataflowConnectTransformationProperties;
                    }
                }
            };
            checkAndReplaceReservedWordsInAdfAppModelConfigurationItem(pluginMetadata.adfAppModelConfiguration?.linkedService);
            checkAndReplaceReservedWordsInAdfAppModelConfigurationItem(pluginMetadata.adfAppModelConfiguration?.location);
            checkAndReplaceReservedWordsInAdfAppModelConfigurationItem(pluginMetadata.adfAppModelConfiguration?.storeReadSettings);
            checkAndReplaceReservedWordsInAdfAppModelConfigurationItem(pluginMetadata.adfAppModelConfiguration?.storeWriteSettings);
            checkAndReplaceReservedWordsInAdfAppModelConfigurationItem(pluginMetadata.adfAppModelConfiguration?.dataset);
            checkAndReplaceReservedWordsInAdfAppModelConfigurationItem(pluginMetadata.adfAppModelConfiguration?.copySource);
            checkAndReplaceReservedWordsInAdfAppModelConfigurationItem(pluginMetadata.adfAppModelConfiguration?.copySink);
            checkAndReplaceReservedWordsInAdfAppModelConfigurationItem(pluginMetadata.adfAppModelConfiguration?.dataflowInlineSource);
            checkAndReplaceReservedWordsInAdfAppModelConfigurationItem(pluginMetadata.adfAppModelConfiguration?.dataflowInlineSink);

            this._propertyNameToResourceNameMapping.set(pluginMetadata.connectorName, propertyNameToResourceNameMapping);
        }
    }

    public getResourceName(connectorName: string, propertyName: string): string {
        return this._propertyNameToResourceNameMapping.get(connectorName)?.get(propertyName);
    }

    private _isReservedWord(propertyName: string): boolean {
        return PluginMetadataProcessService.RESERVED_WORDS.has(propertyName);
    }
}

// Get the latest reserved words dynamically
try {
    PluginMetadataProcessService.RESERVED_WORDS = new Set(
        Reflect.ownKeys(Reflect.getPrototypeOf(Reflect.construct(Owned, [])))
            .concat(Reflect.ownKeys(Reflect.getPrototypeOf(Reflect.construct(Named, []))))
            .concat(Reflect.ownKeys(Reflect.construct(Owned, [])))
            .concat(Reflect.ownKeys(Reflect.construct(Named, [])))
            .concat(...PluginMetadataProcessService.RESERVED_WORDS.values())
            .filter((key) => typeof key === 'string') as string[]
    );
} catch {
    // ignore
}
